import React, { useState, useEffect } from 'react'

import { useFirebaseAuthEmailStatus } from '@components/pages/PurchaseFormPage/useFirebaseAuthEmailStatus'
import { usePasswordIcon } from '@components/pages/PurchaseFormPage/usePasswordIcon'
import { useCreateCreditCard } from '@components/atoms/CreditCardForm/useCreateCreditCard'
import { SubmitHandler, useForm } from 'react-hook-form'
import { css } from '@emotion/react'
import {
    usePostOrderConfirmation,
    UserAddress,
} from '@/network/api/orderConfirmation'
import {
    CustomConfirmationFromHtmlDataset,
    OrderConfirmationInfo,
} from '../../organisms/OrderConfirmationModal'

import { ErrResult } from '@/network/fetcher'
import { Item } from '@/network/api/item'
import { usePushDataLayerOnce } from '@/utils/analytics'
import { PaymentMethodEnum } from '@/@types/scheme'
import { pTagStyle } from '@/styles/global'

const mailAddressMsgStyle = css({
    display: 'flex',
    flexWrap: 'wrap',
    margin: '6px 0 12px',
})

const txtLinkStyle = css({
    color: '#2eb6aa',
    margin: '0 0 0 auto',
})

export type FormFieldValue = {
    email: string
    password: string
    variantItemCode?: string
    paymentMethod: PaymentMethodEnum
    isSubscribeMailmagazine: boolean
    billAddress: UserAddress
    shipAddress?: UserAddress // isUseAnotherAddressForShipがtrueだと取れる
    couponCode: string | undefined
    mzdaoCode: string | null
}

export type PurchaseFormPageProps = {
    item: Item
    htmlDataset: CustomConfirmationFromHtmlDataset
    setPage: React.Dispatch<React.SetStateAction<Page>>
    setErrResult: React.Dispatch<React.SetStateAction<ErrResult | undefined>>
}

export const useViewModel = ({
    setErrResult,
    item,
    htmlDataset,
}: PurchaseFormPageProps) => {
    // フォーム全体
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        setError,
        control,
        formState: { errors },
    } = useForm<FormFieldValue>({
        defaultValues: {
            isSubscribeMailmagazine: true,
            paymentMethod: PaymentMethodEnum.Creditcard,
        },
    })
    const pushDataLayerOnce = usePushDataLayerOnce()
    // バリアント使うかどうか
    const shouldSelectVariant =
        htmlDataset.isVisibleVariant && item && item.variants.length > 0
    // メールフォーム関連
    const [mailAddressMsg, setMailAddressMsg] = useState<
        React.ReactNode | undefined
    >()
    const emailStatus = useFirebaseAuthEmailStatus()
    useEffect(() => {
        setError('email', {})
        if (emailStatus.authMethodStatus === 'newUser') {
            setMailAddressMsg(undefined)
        }
        if (emailStatus.authMethodStatus === 'authBySns')
            setMailAddressMsg(
                <div css={mailAddressMsgStyle}>
                    <p css={[pTagStyle, css({ color: '#DC3251' })]}>
                        このメールアドレスはパスワードの設定が必要です。
                    </p>
                    <a
                        css={txtLinkStyle}
                        href="https://id.vitanote.jp/password_reset_email"
                    >
                        {' '}
                        パスワードの再設定はこちら
                    </a>
                </div>
            )
        if (emailStatus.authMethodStatus === 'invalidEmail')
            setError('email', {
                type: 'manual',
                message: '有効なメールアドレスをご入力ください。',
            })

        // authMethodStatusの状態のみ監視
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailStatus.authMethodStatus])
    // パスワードフォーム関連
    const { PasswordIcon, passwordInputType } = usePasswordIcon()
    // クレジットカードフォーム関連
    const { createCreditCard, stripeError, creditCard } = useCreateCreditCard()
    const [selectedPaymentMethod, setSelectedPaymentMethod] =
        useState<PaymentMethodEnum>(PaymentMethodEnum.Creditcard)

    // 請求先と届け先が違う
    const [isUseAnotherAddressForShip, setIsUseAnotherAddressForShip] =
        useState<boolean>(false)
    // 注文の確認
    const [orderConfirmationInfo, setOrderConfirmationInfo] = useState<
        OrderConfirmationInfo | undefined
    >(undefined)
    const [isLockedClickOrderConfirmation, setIsLockedClickOrderConfirmation] =
        useState<boolean>(false) // 二段送信防止
    const postOrderConfirmation = usePostOrderConfirmation()
    const onSubmitConfirm: SubmitHandler<FormFieldValue> = async (data) => {
        setIsLockedClickOrderConfirmation(true)
        setTimeout(() => {
            setIsLockedClickOrderConfirmation(false)
        }, 3000)
        const {
            password,
            email,
            shipAddress,
            variantItemCode,
            paymentMethod,
            couponCode,
            mzdaoCode,
            ...postData
        } = JSON.parse(JSON.stringify(data)) as FormFieldValue
        // アカウントあるのにログイン失敗するのはここで弾く
        const loginStatus = await emailStatus.checkLoginBeforeOrderConfirm(
            email,
            password
        )
        if (loginStatus === 'loginFailure') {
            setError('password', {
                type: 'missPassword',
                message: 'パスワードが間違っています。',
            })
            return
        }

        // 支払い情報を設定する
        if (paymentMethod === PaymentMethodEnum.Creditcard) {
            const creditCardResult = await createCreditCard()
            console.error('createCreditCard is invalid')
            if (!creditCardResult) {
                setError('paymentMethod', {
                    type: 'stripeError',
                    message: 'クレジットカード情報をご確認ください',
                })
                return
            }
        } else if (paymentMethod !== PaymentMethodEnum.Atobarai) {
            console.error('paymentMethod is invalid')
        }

        // 商品がなければリクエストできない
        if (!item) {
            console.error('item is invalid')
            return
        }
        // variantsに配列があるのに、variantが選ばれていなければ、リクエストできない
        if (shouldSelectVariant && !variantItemCode) {
            console.error('variantItemCode is invalid')
            return
        }
        const itemCode =
            shouldSelectVariant && variantItemCode ? variantItemCode : item.code

        if (isUseAnotherAddressForShip && shipAddress) {
            await postOrderConfirmation.request({
                body: {
                    ...postData,
                    shipAddress,
                    itemCode,
                    paymentMethod,
                    couponCode,
                },
                query: undefined,
            })
        } else {
            await postOrderConfirmation.request({
                body: {
                    ...postData,
                    paymentMethod,
                    couponCode,
                    shipAddress: postData.billAddress,
                    itemCode,
                },
                query: undefined,
            })
        }
    }
    useEffect(() => {
        if (
            postOrderConfirmation.data &&
            !postOrderConfirmation.loading &&
            postOrderConfirmation.lastRequestedData
        ) {
            setOrderConfirmationInfo({
                ...getValues(),
                ...postOrderConfirmation.data,
                userIdToken: emailStatus.userIdToken,
                creditCard: creditCard || null,
                shipAddress:
                    postOrderConfirmation.lastRequestedData.body.shipAddress, // リクエストしたものが正しいのでココを参照している。
            })
        }
        if (postOrderConfirmation.error && !postOrderConfirmation.loading) {
            setErrResult(postOrderConfirmation.error)
        }
        // リクエスト完了時のみ監視
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postOrderConfirmation.loading])

    return {
        errors,
        onSubmitConfirm,
        register,
        handleSubmit,
        setValue,
        getValues,
        setError,
        isLockedClickOrderConfirmation,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        orderConfirmationInfo,
        shouldSelectVariant,
        mailAddressMsg,
        emailStatus,
        PasswordIcon,
        passwordInputType,
        createCreditCard,
        stripeError,
        pushDataLayerOnce,
        control,
        isUseAnotherAddressForShip,
        setIsUseAnotherAddressForShip,
        setOrderConfirmationInfo,
        postOrderConfirmation,
    }
}
